import moment from "moment";

// Upload (file) status from owner_bulk_upload_batch_view
export const uploadStatusMeta: Record<string, { label: string; className: string }> = {
  COMMITTED: { label: "Imported", className: "bg-green-100 text-green-700" },
  VALIDATED: { label: "Validated only", className: "bg-gray-100 text-gray-600" },
};

// Row outcome from owner_bulk_upload_audit
export const rowStatusMeta: Record<string, { label: string; className: string; indicator: string }> = {
  NEW_USER: { label: "New user", className: "bg-green-100 text-green-700", indicator: "bg-green-600" },
  MERGED: { label: "Linked", className: "bg-blue-100 text-blue-700", indicator: "bg-blue-600" },
  SKIPPED: { label: "Skipped", className: "bg-gray-100 text-gray-600", indicator: "bg-gray-500" },
  ERROR: { label: "Error", className: "bg-red-100 text-red-600", indicator: "bg-red-600" },
};

// Row filter chips: value is the status, or WARNING for rows imported with warnings
export const rowFilterOptions = [
  { value: "ALL", label: "All" },
  { value: "NEW_USER", label: "New user", indicator: rowStatusMeta.NEW_USER.indicator },
  { value: "MERGED", label: "Linked", indicator: rowStatusMeta.MERGED.indicator },
  { value: "SKIPPED", label: "Skipped", indicator: rowStatusMeta.SKIPPED.indicator },
  { value: "ERROR", label: "Error", indicator: rowStatusMeta.ERROR.indicator },
  { value: "WARNING", label: "With warnings", indicator: "bg-yellow-500" },
];

export const formatDateTime = (value?: string | null) =>
  value ? moment(value).format("MM/DD/YYYY hh:mm A") : "-";

/** Saves an axios blob response under the given name. */
export const saveBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** attachmentRowData is stored as a JSON object of the Excel header -> cell value. */
export const parseRowData = (raw: any): [string, string][] => {
  if (!raw) return [];
  try {
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Object.entries(data ?? {}).map(([key, value]) => [key, value == null ? "" : String(value)]);
  } catch {
    return [];
  }
};

const csvCell = (value: any) => {
  const text = value == null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Audit rows as CSV: outcome columns first, then every original Excel column. */
export const auditRowsToCsv = (rows: any[]) => {
  const sourceColumns: string[] = [];
  const parsed = rows.map((row) => {
    const values = parseRowData(row?.attachmentRowData);
    values.forEach(([key]) => {
      if (!sourceColumns.includes(key)) sourceColumns.push(key);
    });
    return Object.fromEntries(values);
  });
  const header = ["Row", "Outcome", "Has Warning", "User", "Property", "Property ID", "Message", "Imported By", "Imported At", ...sourceColumns];
  const lines = rows.map((row, i) => [
    row?.attachmentRowNumber,
    rowStatusMeta[row?.status]?.label ?? row?.status,
    row?.hasWarning ? "Yes" : "No",
    row?.userFullName,
    row?.propertyName,
    row?.propertyDisplayId,
    row?.message,
    row?.createdByName,
    formatDateTime(row?.createdAt),
    ...sourceColumns.map((key) => parsed[i][key]),
  ].map(csvCell).join(","));
  return "﻿" + [header.map(csvCell).join(","), ...lines].join("\r\n");
};
